/**
 * useChronicleBulkSyncEvents — subscribes to all bulk_download:* and
 * session_engine:* events from Rust, updates Jotai atoms.
 *
 * Purely reactive: no business logic, just atom updates.
 * Also handles:
 * - session_engine:token_needed → refresh token → provide_token
 * - session_engine:session_created → invalidate TanStack Query cache
 */
import { listen, type Event, type UnlistenFn } from '@tauri-apps/api/event';
import i18next from 'i18next';
import { getDefaultStore, useSetAtom, useStore } from 'jotai';
import { useEffect, useRef } from 'react';

import {
  chronicleAwaitingFinalNoteAtom,
  chronicleInterruptedSessionAtom,
  chroniclePureOfflineSessionsAtom,
} from '@/atoms/chronicle';
import {
  bulkSyncActiveEngineTaskIdsAtom,
  bulkSyncAudioUploadProgressAtom,
  bulkSyncCompletedTaskCountAtom,
  bulkSyncCompletedTaskIdsAtom,
  bulkSyncCumulativeProcessedAtom,
  bulkSyncDiscoveredMaxAtom,
  bulkSyncDownloadCompleteAtom,
  bulkSyncDownloadProgressAtom,
  bulkSyncErrorAtom,
  bulkSyncExpectedUploadTotalAtom,
  bulkSyncNoteFailedCountAtom,
  bulkSyncPendingDrainCountAtom,
  bulkSyncPhaseAtom,
  bulkSyncUploadProgressAtom,
  resetBulkSyncAtomsAtom,
  usbAccessDeniedAtom,
  usbConnectedAtom,
  usbMountPathAtom,
  wifiConnectionStateAtom,
  wifiSsidAtom,
  type BulkSyncPhase,
} from '@/atoms/chronicle/bulk-sync';
import { chronicleDeviceStateAtom } from '@/atoms/chronicle/connection';
import { registerChronicleSessionDeviceFileAtom } from '@/atoms/chronicle/session-device-files';
import { useIsAppsRustAuth0TokenRefreshDesktopEnabled } from '@/features/flags/rust-auth0-token-refresh-desktop';
import { invalidateInterruptedSessions } from '@/hooks/query/desktop/chronicle/useQueryInterruptedSessions';
import { invalidateSessionsQuery } from '@/hooks/query/useQueryCombinedSessions';
import { invalidateTranscriptBySessionId } from '@/hooks/query/useQueryTranscriptBySessionId';
import { getCurrentUser } from '@/hooks/query/useQueryUser';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { autoSessionBridge } from '@/tauri/chronicle/auto-session';
import { invalidateSessionById } from '@heidi/session/data';

import { track } from '@/lib/analytics/track';
import { bulkSyncBridge } from '@/tauri/chronicle/bulk-sync';
import { isTauri } from '@/tauri/utils';
import type {
  AutoBulkSyncTokenNeededEvent,
  AutoSessionTokenNeededEvent,
  BulkDownloadCompleteEvent,
  BulkDownloadFileCrcCheckEvent,
  BulkDownloadFileDecryptionEvent,
  BulkDownloadFileDownloadedEvent,
  BulkDownloadFilesDiscoveredEvent,
  BulkDownloadKeyUnavailableEvent,
  BulkDownloadObservabilityEvent,
  BulkDownloadProgressEvent,
  BulkDownloadSessionTerminalSkipEvent,
  SessionEngineAllCompleteEvent,
  SessionEngineChunkUploadProgressEvent,
  SessionEngineNoteFailedEvent,
  SessionEngineNoteTriggeredEvent,
  SessionEngineProcessingEvent,
  SessionEngineSessionCreatedEvent,
  SessionEngineSource,
  SessionEngineTaskFailedEvent,
  SessionEngineTokenNeededEvent,
  SessionEngineUploadCompleteEvent,
} from '@/types/bulk-sync';
import { logHardwareDatadogEvent } from '@/utils/analytics/log-hardware-datadog-event';
import { SESSION_SOURCE_TYPE } from '@/utils/analytics/session-source-type';
import { stripDeviceFileNameForAnalytics } from '@/utils/analytics/strip-device-file-name';
import {
  trackHardwareBulkSyncCancelled,
  trackHardwareBulkSyncFileCrcChecked,
  trackHardwareBulkSyncTaskFailed,
  trackHardwareBulkSyncTriggered,
  trackHardwareFileDecryption,
  trackHardwareSessionCreated,
  trackHardwareUsbAccessDenied,
} from '@/utils/analytics/track-hardware-events';
import { chronicleLogger as logger } from '@/utils/logger/chronicle-logger';

type ChunkedInventoryMeta = {
  chunk_count?: number;
  file_size_bytes?: number;
  duration_seconds?: number;
};

// A discovered recording can fail to upload on every ~5s reconcile sweep
// (corrupt audio → 422, server-deleted session → 404) or be a terminal skip
// (zero-chunk) that is counted in the device-discovered total but never emits
// `upload_complete`. Either way `completed` never reaches the denominator, so
// without a fallback the widget sits on "uploading N of M" forever. These
// bound that: a recording that fails this many sweeps in a row, or a run that
// makes no progress for this many sweeps, resolves to a bounded "N failed".
const STALL_FAILURES = 3;
const STALL_SWEEPS = 4;
// ~450s of ~5s sweeps — deliberately above the engine's worst-case per-task
// 429 hold total (3 × 120s + request time), so the widget cannot resolve a
// run whose engine is still legitimately waiting out a rate limit.
const ACTIVE_ENGINE_STALL_SWEEPS = 90;
// Failure-reason sweeps and watchdog samples are weaker stall evidence than a
// successful probe that found work but saw no progress: a short discovery blip
// between recordings of a multi-recording run must not resolve a healthy run.
const FAILED_STALL_TICKS = 12;
const STALL_WATCHDOG_INTERVAL_MS = 60_000;
// A live engine task with a silent transport gets its own long sample budget
// (~25 min): non-chunked engine steps (single-file upload, hardware
// transcription, note generation) emit no interim FE events, so several
// minutes of silence is normal for a healthy task — the watchdog exists to
// catch the wedged tail, not to outpace a slow upload.
const ACTIVE_WATCHDOG_QUIET_SAMPLES = 25;

// Which observation is ticking the stall heartbeat — determines how much quiet
// evidence is needed before the run resolves.
type StallTick = 'sweep-complete' | 'sweep-failure' | 'watchdog';

interface UseChronicleBulkSyncEventsOptions {
  enabled?: boolean;
  getToken: () => string | null;
  getBaseUrl: () => string;
}

export function useChronicleBulkSyncEvents({
  enabled = true,
  getToken,
  getBaseUrl,
}: UseChronicleBulkSyncEventsOptions) {
  // Contextual store so the hook reads from the same Jotai instance that
  // tests wrap it in. `getDefaultStore()` would skip the test wrapper.
  const jotaiStore = useStore();
  const setPhase = useSetAtom(bulkSyncPhaseAtom);
  const setDownloadProgress = useSetAtom(bulkSyncDownloadProgressAtom);
  const setUploadProgress = useSetAtom(bulkSyncUploadProgressAtom);
  const setAudioUploadProgress = useSetAtom(bulkSyncAudioUploadProgressAtom);
  const setExpectedUploadTotal = useSetAtom(bulkSyncExpectedUploadTotalAtom);
  const setCumulativeProcessed = useSetAtom(bulkSyncCumulativeProcessedAtom);
  const setError = useSetAtom(bulkSyncErrorAtom);
  const setNoteFailedCount = useSetAtom(bulkSyncNoteFailedCountAtom);
  const setInterruptedSession = useSetAtom(chronicleInterruptedSessionAtom);
  const setAwaitingFinalNote = useSetAtom(chronicleAwaitingFinalNoteAtom);
  const setPureOfflineSessions = useSetAtom(chroniclePureOfflineSessionsAtom);
  const resetAtoms = useSetAtom(resetBulkSyncAtomsAtom);
  const setUsbConnected = useSetAtom(usbConnectedAtom);
  const setUsbMountPath = useSetAtom(usbMountPathAtom);
  const setUsbAccessDenied = useSetAtom(usbAccessDeniedAtom);
  const setWifiConnectionState = useSetAtom(wifiConnectionStateAtom);
  const setWifiSsid = useSetAtom(wifiSsidAtom);
  const registerSessionDeviceFile = useSetAtom(registerChronicleSessionDeviceFileAtom);

  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const getBaseUrlRef = useRef(getBaseUrl);
  getBaseUrlRef.current = getBaseUrl;
  // Re-lock the engine force-refresh gate on each FE token re-provide.
  const rustAuth0TokenRefresh = useIsAppsRustAuth0TokenRefreshDesktopEnabled();
  const rustAuth0TokenRefreshRef = useRef(rustAuth0TokenRefresh);
  rustAuth0TokenRefreshRef.current = rustAuth0TokenRefresh;
  const hasTrackedStartRef = useRef(false);
  // Per-run device-file metadata for `hardware_session_created`.
  // Durations come from `bulk_download:files_discovered` (replaced each run);
  // sizes from `bulk_download:file_downloaded` (deleted once consumed by the
  // matching `session_engine:session_created`). Both keyed by device filename.
  // The size map is never bulk-cleared: the engine keeps creating sessions
  // after `bulk_download:complete` and across reconciler sweeps, so clearing
  // at any run boundary would race a pending `session_created`. Leftover
  // entries (files whose session creation never succeeds) are bounded by
  // unique device filenames per app session — a few bytes each.
  const discoveredFileDurationsRef = useRef<Map<string, number>>(new Map());
  const downloadedFileSizesRef = useRef<Map<string, number>>(new Map());
  const discoveredFilesRef = useRef<BulkDownloadFilesDiscoveredEvent['files'] | null>(
    null
  );
  const chunkedInventoryRef = useRef<Map<string, ChunkedInventoryMeta>>(new Map());
  // Device directories whose LIST_CHUNK came back empty — counted in the
  // sweep's `discovered` before any chunk list exists, but never uploadable.
  // Keyed like the chunked inventory (`sess_<id>`).
  const zeroChunkSessionsRef = useRef<Set<string>>(new Set());
  // Stems parked by an earlier run. The sweep still walks and re-skips them, so
  // they sit in every later run's `discovered` while being work no run can
  // finish. Kept apart from the zero-chunk set because a park is permanent for
  // the run — a later chunk listing must not pull it back in.
  const priorParkSessionsRef = useRef<Set<string>>(new Set());
  // 'unknown' until first BulkDownloadProgressEvent observed; avoids mislabeling
  // pre-handshake failures as BLE-specific (see APP-4934 plan A.2).
  const lastTransferMethodRef = useRef<'ble' | 'wifi' | 'usb' | 'unknown'>('unknown');
  const completeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeChunkUploadTaskIdRef = useRef<string | null>(null);
  const lastLoggedUploadedChunksRef = useRef(0);
  // Count bulk-sync sessions by device-file identity (`task_id`), deduped and
  // scoped to Chronicle, so the widget reflects only the real recordings on the
  // device — not the shared engine's cross-source, additive
  // `all_complete.total_processed` (which re-counted the same file across
  // reconciler re-sweeps and folded in offline-recording tasks).
  // The denominator is the DEVICE-discovered recording count (`discoveredMax`);
  // `completed` = recordings uploaded (chronicle `upload_complete`). The run
  // finishes when `completed` reaches the device-discovered total — never on an
  // engine task-failure, which the bulk-sync layer transparently retries (a
  // failed → re-submitted → uploaded recording ends in `completed`, so the
  // widget shows `complete`, never a latched `error`). The numeric atoms derive
  // from the discovered total + the completed-set size.
  // Run denominator = the DEVICE-discovered recording count (the probe's
  // `discovered`/`totalFiles`), captured as a max for the whole run. The chunked
  // path syncs recordings sequentially (one per reconcile sweep), so the engine
  // never holds the full set at once — only the device probe knows the real
  // total. Kept as a max so it doesn't shrink as recordings sync + get deleted
  // and later sweeps report fewer discovered.
  // Stored in Jotai atoms (not hook refs) so completion state survives a
  // provider/hook remount for as long as the widget counters do.
  // Recovery-drain bookkeeping: net re-submitted recordings (count − skips). The
  // drain has no device download, so this is the drain run's denominator and also
  // detects an all-skipped drain (→ idle).

  // Stall detection (so the run can't hang at "uploading N of M"):
  // - failureCountsRef: consecutive task_failed per task_id (cleared on its
  //   upload_complete) — catches a recording that keeps failing every sweep.
  // - bulkSyncActiveEngineTaskIdsAtom: engine tasks that have started but have
  //   not emitted upload_complete/task_failed yet. While this is non-empty, a
  //   quiet tick means the backend is still processing, not terminal stall. An
  //   atom (not a ref) so a remount can't demote a live upload to the short
  //   idle budget; the quiet counters below stay refs — restarting a budget
  //   only delays resolution, the safe direction.
  // - stuckSweepsRef: quiet observations with no progress while uploading —
  //   catches terminal skips that emit no task_failed (reset by an active
  //   download / completion / chunk-upload progress, so a healthy or
  //   actively-downloading run won't trip).
  // - activeEngineQuietSweepsRef: a longer safety net for a task that emits
  //   processing but never reaches upload_complete/task_failed.
  // - stallResolvedRef: sticky once resolved so the ~5s reconciler sweeps don't
  //   bounce the widget back to 'uploading'; lifted by a real upload_complete
  //   or a fresh run observed after the notice was dismissed.
  const failureCountsRef = useRef<Map<string, number>>(new Map());
  const activeEngineQuietSweepsRef = useRef(0);
  // Which refusal is already on screen (or was, until dismissed). The reconciler
  // retries every few seconds while a key stays unresolvable, so the same code is
  // raised once and then left dismissible — but a *different* code is new
  // information and must still get through. Cleared by real progress.
  const lastRaisedKeyErrorRef = useRef<string | null>(null);
  // Watchdog samples on the active-task path accrue separately from sweeps —
  // they measure different clocks (60s vs ~5s) and mixing them would turn the
  // sweep budget into a much shorter wall-clock one.
  const activeWatchdogSamplesRef = useRef(0);
  // Quiet failure-reason sweeps and idle watchdog samples (weak stall
  // evidence, FAILED_STALL_TICKS budget), kept apart from stuckSweepsRef
  // (quiet successful probes, STALL_SWEEPS budget) so a mixed sequence can't
  // reach the strong-evidence threshold on weak ticks.
  const weakQuietTicksRef = useRef(0);
  // True when a reconcile sweep ticked the heartbeat since the last watchdog
  // sample — the watchdog only substitutes for MISSING sweeps; sampling on top
  // of a live sweep cadence would double-count against the stall budgets.
  const sweepSinceLastWatchdogRef = useRef(false);
  // A live recording legitimately silences the reconciler for the whole
  // consult; the watchdog must not read that silence as a stall. Set by the
  // live_recording sweep branch, cleared by any other bulk_download activity.
  const syncPausedForRecordingRef = useRef(false);
  const stuckSweepsRef = useRef(0);
  const lastStallChunksRef = useRef(0);
  const stallResolvedRef = useRef(false);

  useEffect(() => {
    if (!isTauri() || !enabled) return;

    const unlistenFns: UnlistenFn[] = [];

    const refreshSessionListCaches = () => {
      void invalidateSessionsQuery();
    };

    const completedTaskIds = () => jotaiStore.get(bulkSyncCompletedTaskIdsAtom);
    const completedTaskCount = () => jotaiStore.get(bulkSyncCompletedTaskCountAtom);
    const activeEngineTaskIds = () => jotaiStore.get(bulkSyncActiveEngineTaskIdsAtom);
    // Copy-on-write, never in-place: the atom's initial value is one
    // module-level Set shared by every store until a set() replaces it, so an
    // in-place mutation could leak task ids across stores.
    const addActiveEngineTaskId = (taskId: string) => {
      const next = new Set(activeEngineTaskIds());
      next.add(taskId);
      jotaiStore.set(bulkSyncActiveEngineTaskIdsAtom, next);
    };
    const removeActiveEngineTaskId = (taskId: string) => {
      const current = activeEngineTaskIds();
      if (!current.has(taskId)) return;
      const next = new Set(current);
      next.delete(taskId);
      jotaiStore.set(bulkSyncActiveEngineTaskIdsAtom, next);
    };
    const downloadComplete = () => jotaiStore.get(bulkSyncDownloadCompleteAtom);
    const setDownloadComplete = (value: boolean) => {
      jotaiStore.set(bulkSyncDownloadCompleteAtom, value);
    };
    const discoveredMax = () => jotaiStore.get(bulkSyncDiscoveredMaxAtom);
    const setDiscoveredMax = (value: number) => {
      jotaiStore.set(bulkSyncDiscoveredMaxAtom, value);
    };
    const pendingDrainCount = () => jotaiStore.get(bulkSyncPendingDrainCountAtom);
    const setPendingDrainCount = (value: number) => {
      jotaiStore.set(bulkSyncPendingDrainCountAtom, value);
    };
    // `bulkSyncDiscoveredMaxAtom` is a max latch, so a zero-chunk directory can
    // only leave the denominator through a separate subtraction.
    const netDiscovered = () => {
      const excluded = new Set([
        ...zeroChunkSessionsRef.current,
        ...priorParkSessionsRef.current,
      ]);
      return Math.max(0, discoveredMax() - excluded.size);
    };
    const addCompletedTaskId = (taskId: string) => {
      const ids = completedTaskIds();
      if (ids.has(taskId)) return;
      // Internal dedupe state only; the count atom below is the reactive source.
      ids.add(taskId);
      jotaiStore.set(bulkSyncCompletedTaskCountAtom, ids.size);
    };

    // Cleared in lockstep with the numeric-atom reset so each run counts its own
    // distinct device files.
    const resetTaskIdSets = () => {
      jotaiStore.set(bulkSyncCompletedTaskIdsAtom, new Set<string>());
      jotaiStore.set(bulkSyncCompletedTaskCountAtom, 0);
      setDownloadComplete(false);
      setDiscoveredMax(0);
      setPendingDrainCount(0);
      failureCountsRef.current = new Map();
      zeroChunkSessionsRef.current = new Set();
      priorParkSessionsRef.current = new Set();
      jotaiStore.set(bulkSyncActiveEngineTaskIdsAtom, new Set<string>());
      activeEngineQuietSweepsRef.current = 0;
      lastRaisedKeyErrorRef.current = null;
      activeWatchdogSamplesRef.current = 0;
      stuckSweepsRef.current = 0;
      weakQuietTicksRef.current = 0;
      lastStallChunksRef.current = 0;
      stallResolvedRef.current = false;
      syncPausedForRecordingRef.current = false;
    };

    // Denominator = the device-discovered total (device path) or the net drained
    // count (recovery path); numerator = distinct uploaded recordings. Including
    // `done` in the max keeps the denominator >= numerator if counts ever race.
    const runExpectedTotal = () =>
      Math.max(netDiscovered(), pendingDrainCount(), completedTaskCount());

    // Push the deduped counts to the widget atoms.
    const syncUploadCounters = () => {
      setCumulativeProcessed(completedTaskCount());
      setExpectedUploadTotal(runExpectedTotal());
    };

    // Clear run-scoped state atoms AND the widget count atoms when a run resolves
    // to idle without an in-flight upload. Without this, a stale-high
    // `bulkSyncDiscoveredMaxAtom` from an abandoned/stopped run carries into a later
    // smaller run (onDownloadProgress only Math.max-es into it), leaving
    // `maybeFinishUploadRun` unable to reach completion → widget stuck at N/M.
    const resetRunState = () => {
      resetTaskIdSets();
      setExpectedUploadTotal(0);
      setCumulativeProcessed(0);
    };

    if (jotaiStore.get(bulkSyncPhaseAtom) === 'idle') {
      resetTaskIdSets();
    }

    // Finish once the device download is done AND every DISCOVERED recording has
    // uploaded. Because the chunked path syncs recordings one-per-sweep, the
    // denominator must be the device-discovered count (not the engine-submitted
    // count) — otherwise a multi-recording run would finish after the first
    // recording. Never terminates on a failure: an engine task-failure is
    // transient (the bulk-sync layer re-submits the recording), so the run stays
    // 'uploading' until the recording's retry lands in `completed`.
    const maybeFinishUploadRun = (): boolean => {
      if (!downloadComplete()) return false;
      const expected = Math.max(netDiscovered(), pendingDrainCount());
      if (expected === 0) return false;
      if (completedTaskCount() < expected) return false;
      finishUploadRunSuccess();
      return true;
    };

    // A zero-chunk directory is not a session. It stays on the device (no
    // `D_SESS`), so crediting it would report a sync that never happened —
    // it leaves the denominator instead.
    const excludeZeroChunkSession = (key: string) => {
      // Already counted as a real recording this run — its upload owns the slot,
      // so removing it from the denominator would finish the run early.
      const taskId = `${key}.mp3`;
      if (completedTaskIds().has(taskId) || activeEngineTaskIds().has(taskId)) return;
      if (zeroChunkSessionsRef.current.has(key)) return;
      zeroChunkSessionsRef.current.add(key);
      syncUploadCounters();
      maybeFinishUploadRun();
    };

    const excludePriorParkSession = (key: string) => {
      // Already counted as a real recording this run — its upload owns the slot.
      const taskId = `${key}.mp3`;
      if (completedTaskIds().has(taskId) || activeEngineTaskIds().has(taskId)) return;
      if (priorParkSessionsRef.current.has(key)) return;
      priorParkSessionsRef.current.add(key);
      syncUploadCounters();
      maybeFinishUploadRun();
    };

    const reincludeZeroChunkSession = (key: string) => {
      if (!zeroChunkSessionsRef.current.delete(key)) return;
      syncUploadCounters();
    };

    const mergeDiscoveredFiles = (
      files: BulkDownloadFilesDiscoveredEvent['files']
    ): BulkDownloadFilesDiscoveredEvent['files'] =>
      files.map((f) => {
        const key = f.deviceFilename.replace(/\.mp3$/i, '');
        const inv =
          chunkedInventoryRef.current.get(f.deviceFilename) ??
          chunkedInventoryRef.current.get(key);
        const chunkCount = f.chunkCount ?? inv?.chunk_count;
        const fileSizeBytes = f.fileSizeBytes ?? inv?.file_size_bytes;
        const durationSeconds =
          f.durationSeconds ??
          inv?.duration_seconds ??
          (typeof chunkCount === 'number' ? chunkCount * 45 : undefined);
        return {
          ...f,
          chunkCount,
          fileSizeBytes,
          durationSeconds,
        };
      });

    // Fires `hardware_bulk_sync_triggered` + `hardware_bulk_sync_started` once
    // per run. Primary trigger is `bulk_download:files_discovered`; first
    // progress is the fallback.
    const trackBulkSyncRunStart = (
      method: 'ble' | 'wifi' | 'usb' | 'unknown',
      fileCount: number,
      files?: BulkDownloadFilesDiscoveredEvent['files']
    ) => {
      if (hasTrackedStartRef.current) return;
      hasTrackedStartRef.current = true;
      const merged = files ? mergeDiscoveredFiles(files) : undefined;
      trackHardwareBulkSyncTriggered('auto');
      const startedProps = {
        transfer_method: method,
        file_count: fileCount,
        files: merged?.map((f) => ({
          file_name: f.deviceFilename,
          duration_seconds: f.durationSeconds,
          chunk_count: f.chunkCount,
          file_size_bytes: f.fileSizeBytes,
          duration_known: typeof f.durationSeconds === 'number',
        })),
      };
      track({
        event_name: TrackingEventName.HardwareBulkSyncStarted,
        props: startedProps,
        options: { posthog: true },
      });
      logHardwareDatadogEvent(TrackingEventName.HardwareBulkSyncStarted, {
        session_source_type: SESSION_SOURCE_TYPE.BulkSync,
        ...startedProps,
      });
    };

    const maybeTrackChunkedStart = () => {
      const discovered = discoveredFilesRef.current;
      if (!discovered || hasTrackedStartRef.current) return;
      const looksChunked =
        discovered.length > 0 &&
        discovered.every((f) => f.deviceFilename.startsWith('sess_'));
      if (!looksChunked) return;
      if (chunkedInventoryRef.current.size < discovered.length) return;
      trackBulkSyncRunStart(lastTransferMethodRef.current, discovered.length, discovered);
    };

    const onFilesDiscovered = (event: Event<BulkDownloadFilesDiscoveredEvent>) => {
      const { files, totalFiles, transferMethod } = event.payload;
      discoveredFileDurationsRef.current = new Map(
        files.flatMap((f) =>
          typeof f.durationSeconds === 'number'
            ? [[f.deviceFilename, f.durationSeconds] as const]
            : []
        )
      );
      discoveredFilesRef.current = files;
      chunkedInventoryRef.current = new Map();
      lastTransferMethodRef.current = transferMethod ?? lastTransferMethodRef.current;
      const looksChunked =
        files.length > 0 && files.every((f) => f.deviceFilename.startsWith('sess_'));
      if (looksChunked) {
        maybeTrackChunkedStart();
      } else {
        trackBulkSyncRunStart(transferMethod, totalFiles, files);
      }
    };

    const onObservability = (event: Event<BulkDownloadObservabilityEvent>) => {
      const { eventName, level, attributes } = event.payload;
      const sessId = attributes.sess_id;
      const deviceFileName = attributes.device_file_name;
      const key =
        typeof deviceFileName === 'string'
          ? deviceFileName
          : typeof sessId === 'string'
            ? `sess_${sessId}`
            : null;
      if (
        eventName === 'hardware_bulk_sync_session_discovered' ||
        eventName === 'hardware_bulk_sync_chunk_listed'
      ) {
        if (key) {
          chunkedInventoryRef.current.set(key, {
            chunk_count:
              typeof attributes.chunk_count === 'number'
                ? attributes.chunk_count
                : undefined,
            file_size_bytes:
              typeof attributes.total_bytes === 'number'
                ? attributes.total_bytes
                : undefined,
            duration_seconds:
              typeof attributes.duration_seconds === 'number'
                ? attributes.duration_seconds
                : undefined,
          });
          // A later sweep that does list chunks makes the directory a session
          // again, so it returns to the denominator.
          if (typeof attributes.chunk_count === 'number' && attributes.chunk_count > 0) {
            reincludeZeroChunkSession(key);
          }
          maybeTrackChunkedStart();
        }
      } else if (
        key &&
        eventName === 'hardware_bulk_sync_session_skipped' &&
        attributes.reason === 'zero_chunks_reported'
      ) {
        excludeZeroChunkSession(key);
      }
      // `bulk_download:observability` is a device-download-only channel, so the
      // whole forwarded stream is bulk sync regardless of the Rust event name.
      // Strip `device_file_name` for Datadog only — inventory / engine keys above
      // stay on the raw FW string (HAR-1777).
      logHardwareDatadogEvent(
        eventName,
        {
          session_source_type: SESSION_SOURCE_TYPE.BulkSync,
          ...attributes,
          device_file_name: stripDeviceFileNameForAnalytics(
            typeof attributes.device_file_name === 'string'
              ? attributes.device_file_name
              : undefined
          ),
        },
        level
      );
    };

    // Device file size becomes known after download — held until the
    // matching `session_engine:session_created` consumes it.
    const onFileDownloaded = (event: Event<BulkDownloadFileDownloadedEvent>) => {
      downloadedFileSizesRef.current.set(
        event.payload.deviceFilename,
        event.payload.fileSizeBytes
      );
    };

    // Whole-session-empty (and similar terminal skips) inflate discoveredMax
    // but never emit upload_complete. Credit them as completed so stall math
    // does not toast "N files failed to upload".
    const onSessionTerminalSkip = (
      event: Event<BulkDownloadSessionTerminalSkipEvent>
    ) => {
      const taskId = event.payload.deviceFilename;
      if (!taskId) return;
      // Parked before this run started: crediting it again would report a sync
      // that already happened (or never can), so it leaves the denominator on
      // the same tick — both sides move together or the run finishes early.
      if (event.payload.priorPark) {
        excludePriorParkSession(taskId.replace(/\.mp3$/i, ''));
        return;
      }
      addCompletedTaskId(taskId);
      syncUploadCounters();
      maybeFinishUploadRun();
    };

    // Per-file decryption outcome, forwarded 1:1 as
    // `hardware_file_decryption_skipped` / `_success` / `_failed`.
    const onFileDecryption = (event: Event<BulkDownloadFileDecryptionEvent>) => {
      trackHardwareFileDecryption({
        deviceFileName: event.payload.deviceFilename,
        outcome: event.payload.outcome,
        method: event.payload.method,
        reason: event.payload.reason,
      });
    };

    // Firmware-CRC verdict before the device delete, forwarded 1:1 as
    // `hardware_bulk_sync_file_crc_checked`.
    const onFileCrcCheck = (event: Event<BulkDownloadFileCrcCheckEvent>) => {
      trackHardwareBulkSyncFileCrcChecked({
        deviceFileName: event.payload.deviceFilename,
        result: event.payload.result,
        expectedCrc: event.payload.expectedCrc,
        actualCrc: event.payload.actualCrc,
        reason: event.payload.reason,
      });
    };

    const onDownloadProgress = (event: Event<BulkDownloadProgressEvent>) => {
      // A device download is in progress — more recordings may still be
      // submitted, so the run isn't eligible to finish until it completes.
      setDownloadComplete(false);
      // Progress means a sweep resolved a key, so an earlier refusal is stale.
      lastRaisedKeyErrorRef.current = null;
      // An active download is progress, not a quiescent stall — reset the
      // quiet counters (the perpetually-failing-recording case is caught by
      // the per-task failure tally, not these), and download activity means
      // no live recording is blocking the reconciler.
      stuckSweepsRef.current = 0;
      weakQuietTicksRef.current = 0;
      syncPausedForRecordingRef.current = false;
      if (completeTimerRef.current) {
        clearTimeout(completeTimerRef.current);
        completeTimerRef.current = null;
      }
      // A fresh run observed from a dismissed terminal state re-arms stall
      // resolution: the widget's dismiss clears phase/error but cannot reach
      // this ref, and a latched flag would keep the heartbeat disarmed and the
      // progress phase suppressed for every later run.
      if (
        stallResolvedRef.current &&
        jotaiStore.get(bulkSyncPhaseAtom) === 'idle' &&
        jotaiStore.get(bulkSyncErrorAtom) === null
      ) {
        stallResolvedRef.current = false;
        resetTaskIdSets();
      }
      // Backend session creation ('uploading') is sticky: a fresh device
      // download starting mid-session-creation (auto_bulk_sync reconciles
      // every ~5s) must not hide the progress banner. While a run is
      // stall-resolved, the resolved 'error' surface is also sticky — a
      // re-download sweep for the stuck recording must not bounce it back to
      // 'downloading' (the resolution lifts when a retry actually completes).
      if (!stallResolvedRef.current) {
        setPhase((prev) => (prev === 'uploading' ? prev : 'downloading'));
      }
      // payload.transferMethod is the source of truth once the first progress
      // event arrives. Before that we keep `'unknown'` so cancel/fail events
      // emitted between trigger and first progress don't get mislabeled as BLE.
      const method = event.payload.transferMethod ?? lastTransferMethodRef.current;
      lastTransferMethodRef.current = method;
      setDownloadProgress({
        totalFiles: event.payload.totalFiles,
        completedFiles: event.payload.completedFiles,
        currentFileName: event.payload.currentFileName,
        currentBytesTransferred: event.payload.bytesTransferred,
        currentBytesTotal: event.payload.bytesTotal,
        currentTransferMethod: method,
      });

      // The device probe's file count is the run's true denominator (the chunked
      // path only ever submits one recording to the engine at a time).
      setDiscoveredMax(Math.max(discoveredMax(), event.payload.totalFiles ?? 0));
      syncUploadCounters();

      trackBulkSyncRunStart(
        method,
        event.payload.totalFiles,
        discoveredFilesRef.current ?? undefined
      );
    };

    // Stall heartbeat, ticked from EVERY terminal sweep branch and from the
    // watchdog sampler — a parked upload whose sweeps keep dying at discovery
    // used to tick nothing and froze 'uploading' forever. While an upload run
    // is below the device-discovered total, a tick that observes no upload
    // progress accrues toward a stall; enough quiet ticks resolve the run to
    // the retryable error surface. Returns true when it resolved (callers
    // return early so their branch can't disturb the resolved phase).
    const advanceUploadStallHeartbeat = (
      phaseAtEntry: BulkSyncPhase,
      tick: StallTick
    ): boolean => {
      if (tick !== 'watchdog') sweepSinceLastWatchdogRef.current = true;
      if (phaseAtEntry !== 'uploading' || stallResolvedRef.current) return false;
      // A live recording silences the reconciler legitimately — watchdog
      // samples must not accrue until a non-recording sweep clears the pause.
      if (tick === 'watchdog' && syncPausedForRecordingRef.current) return false;
      const expectedNow = Math.max(netDiscovered(), pendingDrainCount());
      if (expectedNow <= completedTaskCount()) return false;

      if (activeEngineTaskIds().size > 0) {
        stuckSweepsRef.current = 0;
        weakQuietTicksRef.current = 0;
        lastStallChunksRef.current = jotaiStore.get(
          bulkSyncAudioUploadProgressAtom
        ).uploadedChunks;
        if (tick === 'watchdog') {
          activeWatchdogSamplesRef.current += 1;
          if (activeWatchdogSamplesRef.current >= ACTIVE_WATCHDOG_QUIET_SAMPLES) {
            resolveStalledRun();
            return true;
          }
        } else {
          activeEngineQuietSweepsRef.current += 1;
          if (activeEngineQuietSweepsRef.current >= ACTIVE_ENGINE_STALL_SWEEPS) {
            resolveStalledRun();
            return true;
          }
        }
        return false;
      }

      // Compare against the PREVIOUS tick's chunk count, not an all-time
      // max: uploadedChunks is per-recording (it resets when the next
      // recording starts uploading), so any change — including a later,
      // smaller recording climbing from 0 — is upload progress. Only a value
      // unchanged across the tick means no upload activity. The baseline is
      // reset to 0 on upload_complete / a newly-submitted recording, so a
      // fresh recording is measured from 0, never against an earlier peak.
      const chunksNow = jotaiStore.get(bulkSyncAudioUploadProgressAtom).uploadedChunks;
      const chunkProgressed = chunksNow !== lastStallChunksRef.current;
      lastStallChunksRef.current = chunksNow;
      if (chunkProgressed) {
        stuckSweepsRef.current = 0;
        weakQuietTicksRef.current = 0;
        return false;
      }
      // Strong and weak evidence accrue separately so a mixed sequence (a few
      // failed sweeps then one quiet successful probe) can't reach the strong
      // 4-tick threshold on the back of weak ticks.
      if (tick === 'sweep-complete') {
        stuckSweepsRef.current += 1;
        if (stuckSweepsRef.current >= STALL_SWEEPS) {
          resolveStalledRun();
          return true;
        }
      } else {
        weakQuietTicksRef.current += 1;
        if (weakQuietTicksRef.current >= FAILED_STALL_TICKS) {
          resolveStalledRun();
          return true;
        }
      }
      return false;
    };

    // Not retryable from the widget: the same resolution runs on every sweep, so
    // the user has to reconnect the device (or sign back in) first.
    const raiseKeyError = (errorCode: string) => {
      lastRaisedKeyErrorRef.current = errorCode;
      logger.error({
        msg: '[bulk_sync] Sweep refused — no trustworthy decryption key',
        errorCode,
      });
      setError({
        phase: 'decrypt',
        failedFiles: 0,
        canRetry: false,
        errorCode,
      });
      setPhase('error');
    };

    const onKeyUnavailable = (event: Event<BulkDownloadKeyUnavailableEvent>) => {
      if (lastRaisedKeyErrorRef.current === event.payload.errorCode) return;
      // A run already in flight copied its key before the store went away and can
      // still finish, so latching `error` over it would leave a card no completion
      // clears. Skipped rather than held: the reconciler re-emits every few
      // seconds, so the next sweep raises it once the run is over.
      const activePhase = jotaiStore.get(bulkSyncPhaseAtom);
      if (activePhase === 'downloading' || activePhase === 'uploading') {
        logger.warn({
          msg: '[bulk_sync] Sweep refused during an active run — deferring to the next sweep',
          errorCode: event.payload.errorCode,
        });
        return;
      }
      raiseKeyError(event.payload.errorCode);
    };

    const onDownloadComplete = (event: Event<BulkDownloadCompleteEvent>) => {
      // auto_bulk_sync used to fire this repeatedly with reason=no_files; the
      // backend now probes the device for files first, so reason=no_files
      // here is rare (essentially only a manual sync against an empty
      // device). Either way this must not wipe in-flight
      // backend-session-creation state.
      const phaseAtEntry = jotaiStore.get(bulkSyncPhaseAtom);
      if (phaseAtEntry !== 'uploading') {
        hasTrackedStartRef.current = false;
        setNoteFailedCount(0);
      }
      const { totalDiscovered, downloaded, skipped, failed, decryptFailed, reason } =
        event.payload;

      // WiFi state must be reset when the download run ends. The backend's
      // TransportTeardownGuard emits wifi:disconnected, but if the
      // WifiDownloader was lost due to async cancellation the event may
      // arrive late or not at all. Resetting here is a safety net.
      setWifiConnectionState('disconnected');
      setWifiSsid(null);

      // Device download outcomes can't clobber 'uploading' — the engine
      // owns that state until onAllComplete terminates it.

      // Any sweep that isn't the live-recording pause means the reconciler is
      // back — re-enable watchdog sampling.
      if (reason !== 'live_recording_active' && reason !== 'live_recording_started') {
        syncPausedForRecordingRef.current = false;
      }

      if (
        reason === 'device_disconnected' ||
        reason === 'usb_disconnected' ||
        reason === 'transport_changed'
      ) {
        // Zero-progress disconnect — never started (e.g. create_initial_transport
        // failed with NOT_CONNECTED). Clean no-op, not an interruption. Same
        // shape as no_files: do NOT dispatch HardwareBulkSyncCancelled or show
        // "Syncing Interrupted" — that inflated the metric with false alarms
        // when BLE probe wedges never transferred anything. Genuine mid-transfer
        // drops always have downloaded > 0 or totalDiscovered > 0.
        const hadNoProgress = (totalDiscovered ?? 0) === 0 && (downloaded ?? 0) === 0;
        // Analytics + progress-clear run BEFORE the heartbeat so the sweep
        // that trips the stall budget is still counted in the cancellation
        // metric — it describes the parked-run population this exit exists
        // for. Only the phase mutations are skipped once the run resolves.
        if (!hadNoProgress) {
          setDownloadProgress((prev) => ({
            ...prev,
            totalFiles: 0,
            completedFiles: 0,
            currentFileName: null,
            currentBytesTransferred: 0,
            currentBytesTotal: 0,
          }));
          // Transient, auto-resuming interruption (not an error): the reconciler
          // re-triggers the sync once the device reconnects, so surface the
          // dismissible "Syncing Interrupted" notice. Leave an in-flight engine
          // upload ('uploading') untouched so it isn't masked. Track it as a
          // cancellation too — transport drops are the most common interruption,
          // so omitting it would skew the device-quality signal.
          trackHardwareBulkSyncCancelled(reason, lastTransferMethodRef.current);
        }
        if (advanceUploadStallHeartbeat(phaseAtEntry, 'sweep-failure')) return;
        if (hadNoProgress) {
          setPhase((prev) => (prev === 'uploading' ? prev : 'idle'));
          if (phaseAtEntry !== 'uploading') resetRunState();
        } else if (phaseAtEntry !== 'uploading') {
          setPhase('interrupted');
        }
      } else if (reason === 'no_files') {
        if (advanceUploadStallHeartbeat(phaseAtEntry, 'sweep-failure')) return;
        // Empty-device outcome — a clean no-op, not a cancellation. Reset
        // phase but do NOT dispatch `HardwareBulkSyncCancelled`: that event
        // is reserved for genuine interruptions (user, recording, transport
        // failure). Treating no_files as cancelled inflated the metric with
        // false alarms — see auto_bulk_sync feedback-loop fix.
        setPhase((prev) => (prev === 'uploading' ? prev : 'idle'));
        if (phaseAtEntry !== 'uploading') resetRunState();
      } else if (
        reason === 'live_recording_active' ||
        reason === 'live_recording_started'
      ) {
        // Transient, auto-resuming interruption (not an error): the reconciler
        // re-triggers the sync once the recording session ends. Leave an
        // in-flight engine upload ('uploading') untouched so it isn't masked.
        // No stall tick here, and the watchdog is paused: a recording
        // legitimately silences the reconciler for the whole consult, and
        // that silence must not read as a stall.
        syncPausedForRecordingRef.current = true;
        // Same zero-progress carve-out as the transport-drop branch above, and
        // for the same reason. On chunk-live the reconciler's probe counts the
        // active session's own on-device file as actionable, so every recording
        // starts a sweep that immediately defers it and stops here having
        // discovered nothing. Calling that "Syncing Interrupted" reports a
        // failure for a sync that never began — which is what a clinician sees
        // moments after starting a recording on a 5.0 device.
        const recordingHadNoProgress =
          (totalDiscovered ?? 0) === 0 && (downloaded ?? 0) === 0;
        if (recordingHadNoProgress) {
          setPhase((prev) => (prev === 'uploading' ? prev : 'idle'));
          if (phaseAtEntry !== 'uploading') resetRunState();
        } else {
          trackHardwareBulkSyncCancelled(reason, lastTransferMethodRef.current);
          if (phaseAtEntry !== 'uploading') {
            setPhase('interrupted');
          }
        }
      } else if (
        reason === 'manual_stop' ||
        reason === 'discovery_failed' ||
        reason === 'cancelled'
      ) {
        // User-initiated stop, discovery failure, or a generic chunked-path
        // cancellation — no auto-resume notice. Tracked BEFORE the heartbeat
        // so the sweep that trips the stall budget still counts in the
        // cancellation metric.
        trackHardwareBulkSyncCancelled(reason, lastTransferMethodRef.current);
        if (advanceUploadStallHeartbeat(phaseAtEntry, 'sweep-failure')) return;
        setPhase((prev) => (prev === 'uploading' ? prev : 'idle'));
        if (phaseAtEntry !== 'uploading') resetRunState();
      } else if (failed > 0 || (decryptFailed ?? 0) > 0) {
        // The device still holds these recordings (download failures retry on a
        // later sweep), so capture the discovered total as the denominator. For
        // a non-in-flight run the reset below clears it back to 0 with the error.
        // Denominator + analytics land BEFORE the heartbeat so the sweep that
        // trips the stall budget still sizes resolveStalledRun's error
        // correctly and still reaches the failure dashboards.
        setDiscoveredMax(Math.max(discoveredMax(), totalDiscovered ?? 0));
        syncUploadCounters();
        const effectiveFailed = Math.max(failed, decryptFailed ?? 0);
        const downloadOnly = effectiveFailed - (decryptFailed ?? 0);
        const phase = downloadOnly > 0 ? 'download' : 'decrypt';
        const failedProps = {
          transfer_method: lastTransferMethodRef.current,
          error: reason ?? phase,
          downloaded,
          remaining: effectiveFailed,
          file_count: totalDiscovered,
        };
        track({
          event_name: TrackingEventName.HardwareBulkSyncFailed,
          props: failedProps,
          // APP-6285: also send to PostHog (was Datadog/Snowplow/Amplitude only).
          options: { posthog: true },
        });
        logHardwareDatadogEvent(
          TrackingEventName.HardwareBulkSyncFailed,
          { session_source_type: SESSION_SOURCE_TYPE.BulkSync, ...failedProps },
          'warn'
        );
        if (advanceUploadStallHeartbeat(phaseAtEntry, 'sweep-failure')) return;
        const messageKey =
          phase === 'decrypt'
            ? 'settings.chronicle.bulkSync.decryptFailed'
            : 'settings.chronicle.bulkSync.downloadFailed';
        // Partial Phase 1 failure: some files DID download and got submitted to
        // the engine (tracked by their `processing` events); the download-phase
        // error is surfaced below while those uploads continue in the background.
        setPhase((prev) => (prev === 'uploading' ? prev : 'error'));
        setError({
          phase,
          message: i18next.t(messageKey, { count: effectiveFailed }),
          failedFiles: effectiveFailed,
          canRetry: true,
        });
        // Reset the run's counters when this download error is actually terminal
        // (we didn't preserve an in-flight upload run via the sticky 'uploading'
        // guard above), so the next run counts only its own recordings.
        if (phaseAtEntry !== 'uploading') {
          setExpectedUploadTotal(0);
          setCumulativeProcessed(0);
          resetTaskIdSets();
          activeChunkUploadTaskIdRef.current = null;
          setAudioUploadProgress({ uploadedChunks: 0, totalChunks: 0 });
        }
      } else {
        // A successful probe (reason='complete') reports the device's recording
        // count — the run's true denominator. Captured here (before the
        // skip-only `downloaded:0` early-return below) so it's seeded even while
        // earlier recordings are still uploading. Max so it survives later
        // sweeps that report fewer files as recordings sync + get deleted.
        setDiscoveredMax(Math.max(discoveredMax(), totalDiscovered ?? 0));
        syncUploadCounters();

        // Stall heartbeat. A successful probe that found work but observed no
        // upload progress is the strongest stall evidence, so this branch
        // trips on the short STALL_SWEEPS budget. This catches terminal skips
        // (counted in the denominator but emitting no task_failed, e.g.
        // zero-chunk). An active download resets it in onDownloadProgress and
        // a real upload_complete resets it, so a draining/healthy run won't
        // trip; a perpetually-failing recording is caught earlier by
        // onTaskFailed.
        if (advanceUploadStallHeartbeat(phaseAtEntry, 'sweep-complete')) {
          return;
        }

        if (downloaded === 0 && failed === 0 && (decryptFailed ?? 0) === 0) {
          // Keep the resolved stall surface ('error') and its run state sticky:
          // a skip-only re-probe of the stuck remainder must not flip to idle or
          // reset the run (the resolution lifts only on a real upload_complete).
          if (stallResolvedRef.current) return;
          setPhase((prev) => (prev === 'uploading' ? prev : 'idle'));
          if (phaseAtEntry !== 'uploading') resetRunState();
          return;
        }

        // Update progress BEFORE phase so the widget sees correct counts
        // on first render when phase transitions to 'complete'.
        setDownloadProgress((prev) => ({
          ...prev,
          totalFiles: totalDiscovered,
          completedFiles: downloaded + skipped,
        }));

        if (completeTimerRef.current) {
          clearTimeout(completeTimerRef.current);
          completeTimerRef.current = null;
        }
        // Device download is done — no more recordings will be submitted, so the
        // run is now eligible to finish. If the engine already uploaded every
        // submitted recording (it may have drained before this fired — the
        // APP-5704 trickled-submit race), finish now; otherwise hand off to the
        // banner and let upload_complete / all_complete finish it once
        // `completed` reaches the device-discovered total.
        setDownloadComplete(true);
        if (!maybeFinishUploadRun()) {
          setPhase((prev) =>
            prev === 'error' || prev === 'interrupted' ? prev : 'uploading'
          );
        }

        const completedProps = {
          transfer_method: lastTransferMethodRef.current,
          downloaded,
          failed,
          skipped,
          file_count: totalDiscovered,
        };
        track({
          event_name: TrackingEventName.HardwareBulkSyncCompleted,
          props: completedProps,
          // APP-6285: also send to PostHog (was Datadog/Snowplow/Amplitude only).
          options: { posthog: true },
        });
        logHardwareDatadogEvent(TrackingEventName.HardwareBulkSyncCompleted, {
          session_source_type: SESSION_SOURCE_TYPE.BulkSync,
          ...completedProps,
        });
      }
    };

    // The recovery drain re-submits failed recordings and has no `downloading`
    // phase, so it has no device-discovered count. `pendingDrainCount` (net of
    // skips) is its denominator; its tasks upload into `completed` like any
    // other. We mark the run download-complete (no more files are coming) so
    // it's eligible to finish.
    const onDrainStarted = (event: Event<{ count: number }>) => {
      const count = event.payload.count;
      if (count <= 0) return;
      const prevPhase = jotaiStore.get(bulkSyncPhaseAtom);
      // A concurrent device download owns the run (its `discoveredMax` is the
      // denominator), so let it drive completion.
      if (prevPhase === 'downloading') return;
      // Clear a prior terminal error + its stale counters so the recovery
      // re-shows progress and a success can clear it; start fresh sets.
      if (prevPhase === 'error') {
        setError(null);
        setExpectedUploadTotal(0);
        setCumulativeProcessed(0);
        setUploadProgress((prev) => ({ ...prev, failedFiles: 0 }));
        resetTaskIdSets();
      }
      setPendingDrainCount(pendingDrainCount() + count);
      setDownloadComplete(true);
      syncUploadCounters();
      setPhase('uploading');
    };

    // Counterpart to onDrainStarted: the drain skipped rows (missing/undecryptable
    // blob) that never reach the engine. If EVERY drained row was skipped, no
    // `processing` will arrive, so resolve to idle here; otherwise the submitted
    // recordings finish the run once `completed` reaches the device-discovered total.
    const onDrainAdjusted = (event: Event<{ skipped: number }>) => {
      const skipped = event.payload.skipped;
      if (skipped <= 0) return;
      setPendingDrainCount(Math.max(0, pendingDrainCount() - skipped));
      syncUploadCounters();
      if (jotaiStore.get(bulkSyncPhaseAtom) !== 'uploading') return;
      // Every drained row skipped (and no concurrent device download or uploads):
      // nothing will reach the engine, so resolve to idle.
      if (
        pendingDrainCount() === 0 &&
        netDiscovered() === 0 &&
        completedTaskCount() === 0
      ) {
        setPhase('idle');
        resetTaskIdSets();
        return;
      }
      maybeFinishUploadRun();
    };

    // --- Session Engine Event Handlers ---
    // Upload and note generation run silently in the background.
    // The widget already shows "complete" after download finishes.
    // We only surface errors (upload failure) or note failures.

    // APP-7096: the session_engine:* channel is SHARED — offline-recording sync
    // runs through the same session_creation_engine and emits the same events.
    // React only to Chronicle-sourced tasks so an offline sync never lights up
    // the Chronicle (Bluetooth) bulk-sync widget; offline sync drives its own
    // surfaces (SyncBadge / SessionListItem) via offline_recording:* events.
    // (all_complete / token_needed carry no per-task source — see those handlers.)
    const isChronicleEvent = (source: SessionEngineSource) => source === 'chronicle';

    const onEngineProcessing = (event: Event<SessionEngineProcessingEvent>) => {
      if (!isChronicleEvent(event.payload.source)) return;
      // Session creation has started for this recording. The run denominator
      // comes from the device probe (`bulkSyncDiscoveredMaxAtom`), not the engine, so
      // there's nothing to count here — the chunked path only ever hands the
      // engine one recording at a time.
      if (completeTimerRef.current) {
        clearTimeout(completeTimerRef.current);
        completeTimerRef.current = null;
      }
      // A newly-submitted recording is fresh upload progress: reset the stall
      // window and the per-recording chunk baseline so it's measured from 0,
      // never against a prior recording's chunk peak.
      addActiveEngineTaskId(event.payload.taskId);
      activeEngineQuietSweepsRef.current = 0;
      activeWatchdogSamplesRef.current = 0;
      stuckSweepsRef.current = 0;
      weakQuietTicksRef.current = 0;
      lastStallChunksRef.current = 0;
      // Stay in 'downloading' while the device is still streaming files —
      // the engine can begin processing file 1 long before files 2..N
      // finish downloading. Flipping to 'uploading' here would hide the
      // sync widget mid-run (APP-5704). The bulk_download:complete handler
      // owns the 'downloading' → 'uploading' transition once all files
      // have been pulled and submitted to the engine.
      setPhase((prev) => {
        if (prev === 'error' || prev === 'interrupted') return prev;
        if (prev === 'downloading') return prev;
        return 'uploading';
      });
      setUploadProgress((prev) => ({
        ...prev,
        totalFiles: event.payload.receivedTasks,
        completedFiles: event.payload.processedTasks,
      }));
      if (event.payload.step === 'chunked_start_segment') {
        activeChunkUploadTaskIdRef.current = event.payload.taskId;
        lastLoggedUploadedChunksRef.current = 0;
        setAudioUploadProgress({ uploadedChunks: 0, totalChunks: 0 });
      }
    };

    const onSessionCreated = (event: Event<SessionEngineSessionCreatedEvent>) => {
      if (!isChronicleEvent(event.payload.source)) return;
      const { taskId, sessionId } = event.payload;
      // iOS parity: the engine emits session_created ONLY
      // for genuinely-new sessions — recovery tasks and dedup-skipped
      // duplicates never reach this — so it is the bulk-sync anchor event for
      // device-file→note tracing. For Chronicle tasks `taskId` IS the device
      // filename. Size/duration ride along when the run captured them
      // (chunked-path recordings have neither).
      if (taskId) {
        const fileSizeBytes = downloadedFileSizesRef.current.get(taskId);
        downloadedFileSizesRef.current.delete(taskId);
        trackHardwareSessionCreated({
          sessionSource: 'bulk_sync',
          deviceFileName: taskId,
          sessionId,
          fileDurationSeconds: discoveredFileDurationsRef.current.get(taskId),
          fileSizeBytes,
        });
        if (sessionId) {
          registerSessionDeviceFile({
            sessionId,
            deviceFileName: taskId,
            sourceType: SESSION_SOURCE_TYPE.BulkSync,
          });
        }
      }
      // Refresh on session_created (fast row visibility) AND upload_complete
      // (status fields + recovery path which skips session_created). React
      // Query coalesces overlapping refetches.
      void invalidateInterruptedSessions();
      refreshSessionListCaches();
    };

    const onUploadComplete = (event: Event<SessionEngineUploadCompleteEvent>) => {
      if (!isChronicleEvent(event.payload.source)) return;
      // Deduped, Chronicle-scoped numerator. Safe to evaluate completion here
      // now that the denominator is the device-discovered count: a single
      // upload_complete can't finish a multi-recording run early because
      // `completed.size` must reach `discoveredMax` (the full device total),
      // which is known up front from the probe — not the sequentially-growing
      // engine-submitted count.
      // A real completion is progress: clear this task's failure tally, reset
      // the quiescence counter, and lift any prior stall resolution so the
      // widget resumes normal progress (a retry finally landed). Move phase out
      // of the resolved 'error' so maybeFinishUploadRun can promote it.
      removeActiveEngineTaskId(event.payload.taskId);
      activeEngineQuietSweepsRef.current = 0;
      activeWatchdogSamplesRef.current = 0;
      failureCountsRef.current.delete(event.payload.taskId);
      stuckSweepsRef.current = 0;
      weakQuietTicksRef.current = 0;
      lastStallChunksRef.current = 0;
      if (stallResolvedRef.current) {
        stallResolvedRef.current = false;
        setError(null);
        setPhase((prev) => (prev === 'error' ? 'uploading' : prev));
      }
      addCompletedTaskId(event.payload.taskId);
      syncUploadCounters();
      setUploadProgress((prev) => ({
        ...prev,
        completedFiles: event.payload.processedTasks,
        totalFiles: event.payload.receivedTasks,
      }));
      // Finish if this was the last discovered recording. The discovered-count
      // gate in maybeFinishUploadRun prevents finishing early between recordings
      // that the chunked path syncs one at a time.
      maybeFinishUploadRun();
      void invalidateInterruptedSessions();
      // Recovery path (live-recording resume via bulk sync) skips
      // session_created + note_triggered, so the session-by-id and
      // transcript caches are only refreshed here.
      invalidateSessionById(event.payload.sessionId);
      void invalidateTranscriptBySessionId(event.payload.sessionId);
      refreshSessionListCaches();
      // the recovered device file is fully uploaded — the server
      // now owns transcription + final-note regen for this session
      // (`/hardware-audio-transcription`), and the caches above are already
      // marked stale so the regenerated note loads on refetch. Lift the
      // awaiting-final-note mask for the matching session; other sessions'
      // uploads leave it untouched.
      setAwaitingFinalNote((prev) =>
        prev && prev.sessionId === event.payload.sessionId ? null : prev
      );
      // the pure-offline marker has served its purpose — the row is
      // deleted with this upload, so the interrupted set no longer contains
      // the session. Drop the marker so it can't suppress a FUTURE genuine
      // interruption of the same session. Kept during a stalled-run resolution
      // (unlike the awaiting mask): a stall doesn't change the session's
      // pure-offline nature, and its row is still pending.
      setPureOfflineSessions((prev) => {
        const sessionId = event.payload.sessionId;
        if (!sessionId || !prev.has(sessionId)) return prev;
        const next = new Set(prev);
        next.delete(sessionId);
        return next;
      });
    };

    const onChunkUploadProgress = (
      event: Event<SessionEngineChunkUploadProgressEvent>
    ) => {
      if (!isChronicleEvent(event.payload.source)) return;
      if (activeChunkUploadTaskIdRef.current !== event.payload.taskId) {
        return;
      }
      activeEngineQuietSweepsRef.current = 0;
      activeWatchdogSamplesRef.current = 0;
      stuckSweepsRef.current = 0;
      weakQuietTicksRef.current = 0;
      lastStallChunksRef.current = event.payload.uploadedChunks;
      const uploaded = event.payload.uploadedChunks;
      const previouslyLogged = lastLoggedUploadedChunksRef.current;
      if (uploaded > previouslyLogged) {
        for (let chunkIndex = previouslyLogged; chunkIndex < uploaded; chunkIndex += 1) {
          const attrs = {
            chunk_source: 'bulk_sync' as const,
            session_source_type: SESSION_SOURCE_TYPE.BulkSync,
            session_id: event.payload.sessionId,
            session_id_local: event.payload.sessionId,
            chunk_index: chunkIndex,
          };
          logHardwareDatadogEvent('hardware_chunk_upload_started', attrs);
          logHardwareDatadogEvent('hardware_chunk_upload_succeeded', attrs);
        }
        lastLoggedUploadedChunksRef.current = uploaded;
      }
      setAudioUploadProgress({
        uploadedChunks: event.payload.uploadedChunks,
        totalChunks: event.payload.totalChunks,
      });
    };

    const onTaskFailed = (event: Event<SessionEngineTaskFailedEvent>) => {
      if (!isChronicleEvent(event.payload.source)) return;
      // A single engine task-failure is transient: the bulk-sync layer re-submits
      // the recording every sweep (a fresh `processing` → `upload_complete` for
      // the same task_id, since `will_retry` is reported false even when it
      // retries), so one failure must NOT latch the widget to `error`. But a
      // recording that fails STALL_FAILURES sweeps in a row WITHOUT an intervening
      // upload_complete is genuinely stuck (corrupt audio → 422, server-deleted
      // session → 404 that won't recover); resolve the run so it can't hang.
      logger.warn({
        msg: '[bulk_sync] Task failed',
        taskId: event.payload.taskId,
        step: event.payload.step,
        error: event.payload.error,
        errorCode: event.payload.errorCode,
        willRetry: event.payload.willRetry,
      });
      // Surface to Amplitude for dashboard grouping (A.2 MUST item — Rust
      // tracing::warn alone is not queryable in product analytics).
      trackHardwareBulkSyncTaskFailed({
        taskId: event.payload.taskId,
        sessionId: event.payload.sessionId ?? undefined,
        step: event.payload.step,
        errorCode: event.payload.errorCode ?? undefined,
        error: event.payload.error,
        willRetry: event.payload.willRetry,
      });
      if (event.payload.step === 'upload' && event.payload.sessionId) {
        logHardwareDatadogEvent(
          'hardware_chunk_upload_failed',
          {
            chunk_source: 'bulk_sync',
            session_source_type: SESSION_SOURCE_TYPE.BulkSync,
            session_id: event.payload.sessionId,
            session_id_local: event.payload.sessionId,
            error_type: event.payload.errorCode,
            reason: event.payload.error,
            retry_count: (failureCountsRef.current.get(event.payload.taskId) ?? 0) + 1,
          },
          'warn'
        );
      }
      // Status moves to upload_failed/decrypt_failed — the row is still in
      // sync_tracking but the displayed state may shift, so refresh.
      void invalidateInterruptedSessions();

      const taskId = event.payload.taskId;
      removeActiveEngineTaskId(taskId);
      activeEngineQuietSweepsRef.current = 0;
      activeWatchdogSamplesRef.current = 0;
      // The failed task's upload is no longer in flight — release its frozen
      // progress bar (it otherwise outlives the run at the last checkpoint)
      // and keep the stall baseline consistent so the reset doesn't read as
      // fresh progress. Scoped to the active chunk task so another task's
      // failure can't wipe a live upload's bar.
      if (activeChunkUploadTaskIdRef.current === taskId) {
        activeChunkUploadTaskIdRef.current = null;
        lastLoggedUploadedChunksRef.current = 0;
        lastStallChunksRef.current = 0;
        setAudioUploadProgress({ uploadedChunks: 0, totalChunks: 0 });
      }
      const consecutiveFails = (failureCountsRef.current.get(taskId) ?? 0) + 1;
      failureCountsRef.current.set(taskId, consecutiveFails);
      if (consecutiveFails >= STALL_FAILURES) {
        const interrupted = jotaiStore.get(chronicleInterruptedSessionAtom);
        if (interrupted?.sessionId) {
          logHardwareDatadogEvent(
            'hardware_recovery_failed',
            {
              session_source_type: SESSION_SOURCE_TYPE.BulkSync,
              session_id: event.payload.sessionId ?? interrupted.sessionId,
              session_id_local: interrupted.sessionId,
              error_code: event.payload.errorCode,
              error_message: event.payload.error,
              attempts: consecutiveFails,
            },
            'error'
          );
        }
        resolveStalledRun();
      }
    };

    // Shared success-termination tail: show the "Syncing complete" card, reset,
    // then drop to idle after 4s. Engine task-failures never terminate the run
    // (they're transparently retried), so success is the only engine-driven
    // terminal — a genuine device/download error is surfaced separately in
    // `onDownloadComplete`.
    const finishUploadRunSuccess = () => {
      // Only ever called when the run is genuinely done (download complete +
      // `completed` reaches the device-discovered total, or an all-skipped drain), so 'downloading' is the
      // trickled-submit case where the engine drained before/at
      // bulk_download:complete — promote it too. 'error'/'interrupted'/'idle'
      // are preserved (APP-6284: a transient interruption isn't a success).
      setPhase((prev) =>
        prev === 'uploading' || prev === 'complete' || prev === 'downloading'
          ? 'complete'
          : prev
      );
      setExpectedUploadTotal(0);
      setCumulativeProcessed(0);
      resetTaskIdSets();
      activeChunkUploadTaskIdRef.current = null;
      lastLoggedUploadedChunksRef.current = 0;
      setAudioUploadProgress({ uploadedChunks: 0, totalChunks: 0 });
      completeTimerRef.current = setTimeout(() => {
        completeTimerRef.current = null;
        setPhase((prev) => (prev === 'complete' ? 'idle' : prev));
      }, 4_000);
    };

    // Terminal fallback when a run can't reach `completed >= expected` because
    // some discovered recordings never emit upload_complete — a recording that
    // fails every sweep (corrupt audio → 422 / deleted server session → 404), or
    // a terminal skip (zero-chunk) counted in the device total. Surfaces a
    // bounded "N failed to upload" (with retry) instead of an indefinite
    // "uploading N of M". Sticks via stallResolvedRef so the ~5s reconciler
    // sweeps don't bounce the widget out of the resolved state until a retry
    // actually lands an upload_complete (which lifts the flag).
    const resolveStalledRun = () => {
      if (stallResolvedRef.current) return;
      stallResolvedRef.current = true;
      // safety valve: the run resolved as unable to complete, so the
      // recovered file's upload_complete may never arrive — lift the
      // awaiting-final-note mask rather than masking the (stale but only
      // available) note forever. The bulk-sync error surface takes over.
      setAwaitingFinalNote(null);
      stuckSweepsRef.current = 0;
      weakQuietTicksRef.current = 0;
      jotaiStore.set(bulkSyncActiveEngineTaskIdsAtom, new Set<string>());
      activeEngineQuietSweepsRef.current = 0;
      activeWatchdogSamplesRef.current = 0;
      activeChunkUploadTaskIdRef.current = null;
      lastLoggedUploadedChunksRef.current = 0;
      setAudioUploadProgress({ uploadedChunks: 0, totalChunks: 0 });
      const expected = Math.max(netDiscovered(), pendingDrainCount());
      const couldNotSync = Math.max(0, expected - completedTaskCount());
      if (couldNotSync === 0) {
        finishUploadRunSuccess();
        return;
      }
      setError({
        phase: 'upload',
        message: i18next.t('settings.chronicle.bulkSync.uploadFailed', {
          count: couldNotSync,
        }),
        failedFiles: couldNotSync,
        canRetry: true,
      });
      setPhase((prev) =>
        prev === 'uploading' || prev === 'downloading' || prev === 'complete'
          ? 'error'
          : prev
      );
    };

    const onAllComplete = () => {
      // A queue drain. We do NOT count `total_processed`/`failed` here: the
      // engine is SHARED with offline-recording sync and this payload is
      // cross-source + per-micro-batch, which is exactly what used to inflate
      // and cross-contaminate the Chronicle counters (APP-7096). The deduped,
      // Chronicle-scoped `submitted`/`completed` sets are the source of truth,
      // so all_complete just re-checks completion. An engine task-failure does
      // NOT terminate the run — the bulk-sync layer re-submits the recording, so
      // we wait for `completed` to reach the device-discovered total (a failed →
      // re-uploaded recording then shows `complete`, never a latched `error`).
      void invalidateInterruptedSessions();
      refreshSessionListCaches();
      maybeFinishUploadRun();
    };

    const onNoteFailed = (event: Event<SessionEngineNoteFailedEvent>) => {
      if (!isChronicleEvent(event.payload.source)) return;
      setNoteFailedCount((prev) => prev + 1);
      // `SessionEngineNoteFailedEvent` carries only a free-text `error`, which
      // can relay a note-generation response body and therefore transcribed
      // clinical content. Keep it out of Datadog; the local logger below still
      // has it for on-device debugging.
      logHardwareDatadogEvent(
        'hardware_note_generation_failed',
        {
          session_source_type: SESSION_SOURCE_TYPE.BulkSync,
          session_id: event.payload.sessionId,
          session_id_local: event.payload.sessionId,
        },
        'warn'
      );
      logger.warn({
        msg: '[bulk_sync] Note generation failed',
        sessionId: event.payload.sessionId,
        error: event.payload.error,
      });
    };

    // Refresh session-by-id (so `consult_note.status` updates) and the legacy
    // transcript cache used by desktop (Tauri forces v2 live transcripts off,
    // see `flags.ts:62-64`) so the recovered transcript shows without restart.
    const onNoteTriggered = (event: Event<SessionEngineNoteTriggeredEvent>) => {
      if (!isChronicleEvent(event.payload.source)) return;
      logHardwareDatadogEvent('hardware_note_generation_triggered', {
        session_source_type: SESSION_SOURCE_TYPE.BulkSync,
        session_id: event.payload.sessionId,
        session_id_local: event.payload.sessionId,
      });
      invalidateSessionById(event.payload.sessionId);
      void invalidateTranscriptBySessionId(event.payload.sessionId);
    };

    const onTokenNeeded = (event: Event<SessionEngineTokenNeededEvent>) => {
      const token = getTokenRef.current();
      const baseUrl = getBaseUrlRef.current();
      if (token) {
        bulkSyncBridge
          .provideToken(
            token,
            baseUrl,
            event.payload.epoch,
            rustAuth0TokenRefreshRef.current
          )
          .catch((error) => {
            logger.error({
              msg: '[bulk_sync] Failed to provide token',
              error: error instanceof Error ? error.message : String(error),
            });
          });
      } else {
        logger.warn({ msg: '[bulk_sync] Token needed but no token available' });
      }
    };

    const onAutoSessionTokenNeeded = (event: Event<AutoSessionTokenNeededEvent>) => {
      const token = getTokenRef.current();
      const baseUrl = getBaseUrlRef.current();
      if (token) {
        void autoSessionBridge.provideAuth(token, baseUrl, event.payload.epoch);
      } else {
        logger.warn({ msg: '[auto_session] Token needed but no token available' });
      }
    };

    const onAutoBulkSyncTokenNeeded = (event: Event<AutoBulkSyncTokenNeededEvent>) => {
      const token = getTokenRef.current();
      const baseUrl = getBaseUrlRef.current();
      const userId = getCurrentUser()?.user_ref_id ?? '';
      const { deviceName, serialNumber } = getDefaultStore().get(
        chronicleDeviceStateAtom
      );
      const deviceSerial = serialNumber ?? deviceName;
      if (token && userId) {
        void bulkSyncBridge.provideAutoSyncAuth(
          token,
          baseUrl,
          userId,
          event.payload.epoch,
          deviceSerial
        );
      } else {
        logger.warn({
          msg: '[auto_bulk_sync] Auth needed but token or user is unavailable',
        });
      }
    };

    const onInterruptedSessionCleared = () => {
      logger.info({
        msg: '[auto_bulk_sync] Interrupted session cleared by Rust — dismissing toast',
      });
      const interrupted = jotaiStore.get(chronicleInterruptedSessionAtom);
      if (interrupted?.sessionId) {
        logHardwareDatadogEvent('hardware_recovery_completed', {
          session_source_type: SESSION_SOURCE_TYPE.BulkSync,
          session_id: interrupted.sessionId,
          session_id_local: interrupted.sessionId,
          recovered_chunks: 0,
          total_chunks: 0,
          latency_from_interrupt_ms: Date.now() - interrupted.timestamp,
        });
      }
      setInterruptedSession(null);
      void invalidateInterruptedSessions();
    };

    // --- USB Volume Event Handlers ---

    const onUsbVolumeMounted = (event: Event<{ mountPath: string }>) => {
      setUsbConnected(true);
      setUsbMountPath(event.payload.mountPath);
      // Drive became readable — clear any prior access-denied state so the
      // toast can re-fire if it later degrades again.
      setUsbAccessDenied(null);
      logger.info({
        msg: '[bulk_sync] USB volume mounted',
        mountPath: event.payload.mountPath,
      });
    };

    const onUsbVolumeUnmounted = () => {
      setUsbConnected(false);
      setUsbMountPath(null);
      // Drive removed — reset access-denied so re-plugging re-evaluates.
      setUsbAccessDenied(null);
      logger.info({ msg: '[bulk_sync] USB volume unmounted' });
    };

    // APP-4668: Windows-only — the OS detected a removable drive but refused
    // to read its RECORD/ directory. Bulk sync stays idle. Sets the
    // access-denied atom so useChronicleNotifications can show a warning toast,
    // logs full diagnostics, and emits an Amplitude event so we can cohort the
    // failure modes (drive_type × os_error) across the install base.
    const onUsbAccessDenied = (
      event: Event<{
        mountPath: string;
        osError: number | null;
        driveType: string | null;
      }>
    ) => {
      const { mountPath, osError, driveType } = event.payload;
      setUsbAccessDenied({ mountPath, osError, driveType });
      logger.warn({
        msg: '[bulk_sync] USB drive detected but OS refused access — bulk sync will not trigger',
        mountPath,
        osError,
        driveType,
      });
      trackHardwareUsbAccessDenied({ osError, driveType });
    };

    // --- WiFi Event Handlers ---

    const onWifiSetupStarted = () => {
      setWifiConnectionState('connecting');
      track({
        event_name: TrackingEventName.HardwareWifiSetupStarted,
        props: {},
      });
    };

    const onWifiConnected = (event: Event<{ ssid: string }>) => {
      setWifiConnectionState('connected');
      setWifiSsid(event.payload.ssid);
      logger.info({
        msg: '[bulk_sync] WiFi connected',
        ssid: event.payload.ssid,
      });
      track({
        event_name: TrackingEventName.HardwareWifiSetupCompleted,
        props: {},
      });
    };

    const onWifiDisconnected = () => {
      setWifiConnectionState('disconnected');
      setWifiSsid(null);
      logger.info({ msg: '[bulk_sync] WiFi disconnected' });
    };

    const onWifiSetupFailed = (event: Event<{ reason: string }>) => {
      setWifiConnectionState('failed');
      logger.warn({
        msg: '[bulk_sync] WiFi setup failed',
        error: event.payload.reason,
      });
      track({
        event_name: TrackingEventName.HardwareWifiSetupFailed,
        props: { error: event.payload.reason },
      });
    };

    // --- Subscribe to all events ---

    let cancelled = false;
    const setup = async () => {
      try {
        const listeners = await Promise.all([
          listen<BulkDownloadFilesDiscoveredEvent>(
            'bulk_download:files_discovered',
            onFilesDiscovered
          ),
          listen<BulkDownloadFileDownloadedEvent>(
            'bulk_download:file_downloaded',
            onFileDownloaded
          ),
          listen<BulkDownloadSessionTerminalSkipEvent>(
            'bulk_download:session_terminal_skip',
            onSessionTerminalSkip
          ),
          listen<BulkDownloadFileDecryptionEvent>(
            'bulk_download:file_decryption',
            onFileDecryption
          ),
          listen<BulkDownloadFileCrcCheckEvent>(
            'bulk_download:file_crc_check',
            onFileCrcCheck
          ),
          listen<BulkDownloadObservabilityEvent>(
            'bulk_download:observability',
            onObservability
          ),
          listen<BulkDownloadProgressEvent>('bulk_download:progress', onDownloadProgress),
          listen<BulkDownloadCompleteEvent>('bulk_download:complete', onDownloadComplete),
          listen<BulkDownloadKeyUnavailableEvent>(
            'bulk_download:key_unavailable',
            onKeyUnavailable
          ),
          listen<{ count: number }>('bulk_download:drain_started', onDrainStarted),
          listen<{ skipped: number }>('bulk_download:drain_adjusted', onDrainAdjusted),
          listen<SessionEngineProcessingEvent>(
            'session_engine:processing',
            onEngineProcessing
          ),
          listen<SessionEngineSessionCreatedEvent>(
            'session_engine:session_created',
            onSessionCreated
          ),
          listen<SessionEngineUploadCompleteEvent>(
            'session_engine:upload_complete',
            onUploadComplete
          ),
          listen<SessionEngineChunkUploadProgressEvent>(
            'session_engine:chunk_upload_progress',
            onChunkUploadProgress
          ),
          listen<SessionEngineTaskFailedEvent>(
            'session_engine:task_failed',
            onTaskFailed
          ),
          listen<SessionEngineAllCompleteEvent>(
            'session_engine:all_complete',
            onAllComplete
          ),
          listen<SessionEngineNoteFailedEvent>(
            'session_engine:note_failed',
            onNoteFailed
          ),
          listen<SessionEngineNoteTriggeredEvent>(
            'session_engine:note_triggered',
            onNoteTriggered
          ),
          listen<SessionEngineTokenNeededEvent>(
            'session_engine:token_needed',
            onTokenNeeded
          ),
          listen<AutoSessionTokenNeededEvent>(
            'chronicle:auto_session_token_needed',
            onAutoSessionTokenNeeded
          ),
          listen<AutoBulkSyncTokenNeededEvent>(
            'chronicle:auto_bulk_sync_token_needed',
            onAutoBulkSyncTokenNeeded
          ),
          listen('chronicle:interrupted_session_cleared', onInterruptedSessionCleared),
          listen<{ mountPath: string }>(
            'chronicle:usb_volume_mounted',
            onUsbVolumeMounted
          ),
          listen('chronicle:usb_volume_unmounted', onUsbVolumeUnmounted),
          listen<{
            mountPath: string;
            osError: number | null;
            driveType: string | null;
          }>('chronicle:usb_access_denied', onUsbAccessDenied),
          listen('wifi:setup_started', onWifiSetupStarted),
          listen<{ ssid: string }>('wifi:connected', onWifiConnected),
          listen('wifi:disconnected', onWifiDisconnected),
          listen<{ reason: string }>('wifi:setup_failed', onWifiSetupFailed),
        ]);
        if (cancelled) {
          for (const unlisten of listeners) {
            unlisten();
          }
        } else {
          unlistenFns.push(...listeners);
        }
      } catch (error) {
        logger.error({
          msg: '[bulk_sync] Failed to setup event listeners',
          error: error instanceof Error ? error.message : String(error),
        });
      }
    };

    void setup();

    // Progress-sampled watchdog: when the transport goes fully silent the
    // reconciler stops sweeping and no bulk_download events arrive at all, so
    // the sweep-fed heartbeat never ticks. The timer only samples — the stall
    // criterion stays progress-based, never time-in-phase — and a sample is
    // skipped whenever a sweep ticked since the last one, so live sweeps and
    // watchdog samples never double-count against the same budget.
    const watchdogTimer = setInterval(() => {
      if (sweepSinceLastWatchdogRef.current) {
        sweepSinceLastWatchdogRef.current = false;
        return;
      }
      advanceUploadStallHeartbeat(jotaiStore.get(bulkSyncPhaseAtom), 'watchdog');
    }, STALL_WATCHDOG_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(watchdogTimer);
      if (completeTimerRef.current) {
        clearTimeout(completeTimerRef.current);
        completeTimerRef.current = null;
      }
      for (const unlisten of unlistenFns) {
        unlisten();
      }
    };
  }, [
    enabled,
    jotaiStore,
    setPhase,
    setDownloadProgress,
    setUploadProgress,
    setAudioUploadProgress,
    setExpectedUploadTotal,
    setCumulativeProcessed,
    setError,
    setNoteFailedCount,
    setInterruptedSession,
    setAwaitingFinalNote,
    setPureOfflineSessions,
    resetAtoms,
    setUsbConnected,
    setUsbMountPath,
    setUsbAccessDenied,
    setWifiConnectionState,
    setWifiSsid,
    registerSessionDeviceFile,
  ]);
}
